import type { CSSProperties } from 'react';
import { IconEdit } from '@wegic/icon';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { AVATAR_URL_PREFIX, DEFAULT_AVATAR_FILENAME } from '@/const';
import { isBlobURL } from '@/utils';
import styles from './index.module.less';

interface AvatarProps {
  type?: 'upload' | 'normal';
  size?: number;
  name?: string;
  image?: string;
  onClick?: (event: React.MouseEvent) => void;
  triggerIcon?: React.ReactNode;
  style?: CSSProperties;
  hasHover?: boolean;
  skipJudge?: boolean;
}

function Avatar(props: AvatarProps) {
  const {
    type = 'normal',
    size = 40,
    name = '',
    image = '',
    triggerIcon,
    onClick,
    style,
    hasHover = true,
    skipJudge = false,
  } = props;
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleError = () => {
    setImgError(true);
  };

  const handleLoad = () => {
    setTimeout(() => {
      setLoading(false);
    }, 100);
  };

  const targetImageSrc = useMemo(() => {
    if (skipJudge) {
      return image;
    }
    const isValidURL = isBlobURL(image) || image.startsWith('http') || image.startsWith('//');
    if (isValidURL) {
      return image;
    }
    if (!image || image === DEFAULT_AVATAR_FILENAME) {
      // 后端返回的默认头像名或者空
      return null;
    }
    return `${AVATAR_URL_PREFIX}/${image}`;
  }, [image]);

  const showImage = targetImageSrc && !imgError;

  useEffect(() => {
    if (!targetImageSrc && name) {
      setLoading(false);
    }
    setImgError(false);
  }, [targetImageSrc, name]);

  return (
    <div
      className={classNames(styles.avatar, { [styles.loading]: loading })}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        ...style,
      }}
      onClick={onClick || undefined}
    >
      { showImage
        ? (
          <img
            className={styles.image}
            alt="avatar"
            src={targetImageSrc}
            onError={handleError}
            onLoad={handleLoad}
            style={{
              opacity: loading ? 0 : 1,
            }}
          />
          )
        : (
          <div
            className={styles.name}
            style={{ backgroundColor: loading && !imgError ? 'transparent' : 'black' }}
          >
            <span>{name.slice(0, 1).toUpperCase()}</span>
          </div>
          )}
      <div className={classNames({
        [styles.mask]: hasHover,
        [styles['upload-mask']]: type === 'upload',
      })}
      >
        {type === 'upload' ? (triggerIcon || <IconEdit style={{ color: 'var(--js-color-white)' }} />) : null}
      </div>
    </div>
  );
}

export default Avatar;
